import { Injectable } from '@angular/core'
import { SwPush } from '@angular/service-worker'
import { MatSnackBar } from '@angular/material/snack-bar'
import { Observable, timer } from 'rxjs'
import { map, take, takeUntil } from 'rxjs/operators'
import { CallerService } from './caller.service'
import { environment } from 'src/environments/environment'

// following the BaseService.LOCAL_STORAGE_DATA_KEY / SESSION_STORAGE_DATA_KEY naming convention
export const PUSH_PROMPT_SHOWN_KEY = 'push-prompt-shown-' + environment.env
export const HAS_POSTED_BEFORE_KEY = 'has-posted-before-' + environment.env

/** Per account, on this device: "Get wishlist alerts on this device too?" was offered (or answered in onboarding). */
export const deviceOfferKey = (corpMemberId: number | string) => `push-device-offer-${environment.env}-${corpMemberId}`

export type PushAvailability = 'available' | 'blocked' | 'unsupported' | 'ios-home-screen' | 'dev-build'

/** What to tell someone when push isn't available (see PushNotificationService.availability). */
export const PUSH_UNAVAILABLE_HELP: Record<Exclude<PushAvailability, 'available'>, string> = {
    blocked: 'Notifications are blocked for this site in your browser. Allow them in your browser\'s site settings (the icon next to the address), then come back and turn this on.',
    'ios-home-screen': 'On iPhone and iPad, add corpers.ng to your Home Screen first (Share → Add to Home Screen), then open it from there and turn this on.',
    unsupported: 'This browser can\'t show push notifications. Try Chrome, Edge or Firefox, or the app from your Home Screen.',
    'dev-build': 'Push notifications only work in production builds - the service worker is off in local dev.',
}

/** Logout waits this long at most for the push cleanup, so a slow network can't hold it up. */
const RELEASE_TIMEOUT_MS = 3000

function readFlag(key: string): boolean {
    try {
        return !!localStorage.getItem(key)
    } catch {
        return true // storage blocked: behave as if already offered, rather than offering every visit
    }
}

function setFlag(key: string): void {
    try {
        localStorage.setItem(key, 'true')
    } catch {
        // Private mode etc. - it just won't be remembered.
    }
}

@Injectable({
    providedIn: 'root',
})
export class PushNotificationService {
    constructor(
        private swPush: SwPush,
        private callerService: CallerService,
        private snackBar: MatSnackBar,
    ) {}

    private vapidPublicKey_CACHE: string | null = null

    isPushApiSupported(): boolean {
        return this.swPush.isEnabled
    }

    /**
     * Whether push can be turned on here, and if not, why - so the UI can say what to do instead of
     * hiding the setting:
     * - 'dev-build': the service worker only runs in production builds (ServiceWorkerModule in AppModule).
     * - 'ios-home-screen': iPhone/iPad Safari only allows push for sites added to the Home Screen.
     * - 'unsupported': this browser has no push.
     * - 'blocked': the user blocked notifications for this site - only browser settings can undo it.
     * - 'available': the toggle will work.
     */
    availability(): PushAvailability {
        if (!environment.production) {
            return 'dev-build'
        }
        const nav = typeof navigator !== 'undefined' ? navigator : null
        const isIos = !!nav && (/iPad|iPhone|iPod/.test(nav.userAgent) || (nav.platform === 'MacIntel' && nav.maxTouchPoints > 1))
        const isStandalone =
            (typeof window !== 'undefined' && window.matchMedia?.('(display-mode: standalone)').matches) || (nav as any)?.standalone === true
        if (isIos && !isStandalone) {
            return 'ios-home-screen'
        }
        if (!this.isPushApiSupported() || typeof Notification === 'undefined') {
            return 'unsupported'
        }
        return Notification.permission === 'denied' ? 'blocked' : 'available'
    }

    /** What to do when push can't be turned on here (see availability()), or null when it can. */
    unavailableHelp(): string | null {
        const availability = this.availability()
        return availability === 'available' ? null : PUSH_UNAVAILABLE_HELP[availability]
    }

    getCurrentSubscription(): Observable<PushSubscription | null> {
        return this.swPush.subscription
    }

    isSubscribed(): Observable<boolean> {
        return this.getCurrentSubscription().pipe(map((sub) => !!sub))
    }

    private async getVapidPublicKey(): Promise<string> {
        if (this.vapidPublicKey_CACHE) {
            return this.vapidPublicKey_CACHE
        }

        const response: any = await this.callerService.getVapidPublicKey().toPromise()
        this.vapidPublicKey_CACHE = response.body.publicKey

        return this.vapidPublicKey_CACHE as string
    }

    async requestPermissionAndSubscribe(): Promise<void> {
        if (!this.isPushApiSupported()) {
            return
        }

        try {
            const serverPublicKey = await this.getVapidPublicKey()
            const subscription = await this.swPush.requestSubscription({ serverPublicKey })

            // A PushSubscription's endpoint belongs to this browser+origin, not to whoever is logged
            // in. So the backend hands the endpoint to this account on save (removing any other
            // account's row for it), and logout releases it (releaseThisDevice) - on a shared phone,
            // nobody gets someone else's notifications.
            await this.callerService.savePushSubscription(subscription.toJSON()).toPromise()
        } catch (error) {
            // Permission denial is an expected path here, not a bug — just let the user know.
            console.error('Could not enable push notifications', error)
            this.callerService.showNotification('Could not enable notifications. You may need to allow notifications in your browser settings.')
        }
    }

    // Shared guard for every contextual trigger (first post, message streak, mention): shows the
    // "Enable notifications?" dialog at most once ever, and only if the user isn't subscribed already.
    async maybePromptToEnable(): Promise<void> {
        if (!this.isPushApiSupported()) {
            return
        }

        if (localStorage.getItem(PUSH_PROMPT_SHOWN_KEY)) {
            return
        }

        const subscribed = await this.isSubscribed().pipe(take(1)).toPromise()

        if (subscribed) {
            return
        }

        // Set the flag immediately (before the dialog's outcome) so a page refresh mid-decision doesn't re-prompt.
        localStorage.setItem(PUSH_PROMPT_SHOWN_KEY, 'true')
        this.callerService.openEnablePushNotificationsDialog()
    }

    /**
     * On logout: stop this device getting the account's notifications. Unsubscribes the browser (so
     * the endpoint dies - the backend prunes it on its next send) and, when the session is still
     * valid (`tellBackend`), removes it on the backend straight away. Never throws, and gives up
     * after RELEASE_TIMEOUT_MS so logout isn't held up.
     */
    async releaseThisDevice({ tellBackend }: { tellBackend: boolean }): Promise<void> {
        if (!this.isPushApiSupported()) {
            return
        }
        const release = async () => {
            const subscription = await this.getCurrentSubscription().pipe(take(1)).toPromise()
            if (!subscription) {
                return
            }
            // Sent before the caller clears the session - the auth interceptor reads the token as it goes.
            const backend = tellBackend
                ? this.callerService.deletePushSubscription({ endpoint: subscription.endpoint }).toPromise().catch(() => {})
                : Promise.resolve()
            await Promise.all([backend, this.swPush.unsubscribe().catch(() => {})])
        }
        await Promise.race([release().catch(() => {}), timer(RELEASE_TIMEOUT_MS).toPromise()])
    }

    /**
     * Once per account on each device: someone using the wishlist on a device that doesn't get
     * notifications (a new phone, or after logging out) is offered them here - onboarding only asks
     * once per account, on whichever device they onboarded. Otherwise their matches only come by
     * email, later.
     */
    async maybeOfferOnThisDevice(corper: { id?: number; onboarding_wishlist_completed_at?: string | null } | null): Promise<void> {
        if (!corper?.id || !corper.onboarding_wishlist_completed_at || !this.isPushApiSupported()) {
            return
        }
        if (typeof Notification !== 'undefined' && Notification.permission === 'denied') {
            return // the browser would refuse without asking
        }
        const key = deviceOfferKey(corper.id)
        if (readFlag(key) || (await this.isSubscribed().pipe(take(1)).toPromise())) {
            return
        }
        setFlag(key)
        this.snackBar
            .open('Get wishlist alerts on this device too?', 'Turn on', { duration: 12000 })
            .onAction()
            .pipe(takeUntil(timer(60000)))
            .subscribe(() => this.requestPermissionAndSubscribe())
    }

    /** Onboarding asked on this device (either answer), so maybeOfferOnThisDevice shouldn't ask again. */
    markOfferedOnThisDevice(corpMemberId: number | undefined): void {
        if (corpMemberId) {
            setFlag(deviceOfferKey(corpMemberId))
        }
    }

    async unsubscribe(): Promise<void> {
        if (!this.isPushApiSupported()) {
            return
        }

        try {
            // `subscription` is a hot observable that never completes, so grab just the latest value.
            const subscription = await this.getCurrentSubscription().pipe(take(1)).toPromise()

            if (!subscription) {
                return
            }

            const endpoint = subscription.endpoint

            await this.swPush.unsubscribe()
            await this.callerService.deletePushSubscription({ endpoint }).toPromise()
        } catch (error) {
            console.error('Could not disable push notifications', error)
            this.callerService.showNotification('Could not disable notifications.')
        }
    }
}
